/**
 * ============================================================================
 * CUSTOM SCRIPTS - MOSCOT THEME
 * ============================================================================
 * 
 * HOW TO ADD NEW FUNCTIONS:
 * 1. Add your function below this comment block
 * 2. Use clear function names (e.g., loadProductData)
 * 3. Add a brief comment above each function
 * 
 * THEME DEFAULT UTILITIES & EXAMPLES
 * ============================================================================
 * 
 * 1. Alpine.js Component Example:
 *    ----------------------------
 *    html: <div x-data="customData()"> ... </div>
 *    js:   function customData() {
 *            return {
 *              open: false,
 *              toggle() { this.open = !this.open }
 *            }
 *          }
 * 
 * 2. Alpine.js Global Store Example:
 *    -------------------------------
 *    js:   Alpine.store('myStore', {
 *            count: 0,
 *            add() { this.count++ }
 *          })
 *    html: <span x-text="$store.myStore.count"></span>
 * 
 * 3. Alpine.data Component Example:
 *    ------------------------------
 *    js:   Alpine.data('dropdown', (initialOpen = false) => ({
 *            open: initialOpen,
 *            init() {
 *               // Logic to run when component initializes
 *            },
 *            toggle() { this.open = !this.open }
 *          }))
 *    html: <div x-data="dropdown(true)"> ... </div>
 * 
 * Documentation: https://alpinejs.dev/directives/data
 * 
 * ============================================================================
 */

// Add your custom functions below this line

document.addEventListener('alpine:init', () => {
  const cachedResults = {};

  Alpine.data('lensBuilder', (
    BuilderPopupId,
    productUrl
    ) => ({
    popupId: BuilderPopupId,
    openBuilderPopup: false,
    contentLoaded: false,
    isLoading: false,
    init() {
      window.addEventListener('lens-builder-drawer:close-popup', () => {
        this.builderPopupRemoveFocus();
      });
    },
    async loadBuilderContent() {
      // Only load if not already loaded
      if (this.contentLoaded || this.isLoading) return;
      
      const cacheKey = productUrl;
      const contentContainer = this.builderPopup.querySelector('.js-builder-content');

      // Check cache first
      if (cachedResults[cacheKey] && contentContainer) {
        contentContainer.innerHTML = cachedResults[cacheKey];
        this.$nextTick(() => {
          if (window.Alpine) {
            window.Alpine.initTree(contentContainer);
          }
        });
        this.contentLoaded = true;
        return;
      }

      this.isLoading = true;
      
      try {
        const url = new URL(`${window.location.origin}${productUrl}`);
        url.searchParams.set('section_id', 'product-lens-builder');
        
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error('Failed to load content');
        
        const sectionHtml = await response.text();
        
        if (sectionHtml && contentContainer) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sectionHtml;
          
          const shopifySection = tempDiv.querySelector('.shopify-section') || tempDiv.querySelector(`[id^='shopify-section']`);
          const htmlToCache = shopifySection ? shopifySection.innerHTML : sectionHtml;

          // Helper to load external resources (CSS/JS)
          const loadExternalResources = async (selector, tagName, attributes) => {
            const elements = tempDiv.querySelectorAll(selector);
            const promises = [];

            elements.forEach(el => {
              const srcOrHref = el.src || el.href;
              if (!srcOrHref) return;
              
              // Skip if already loaded or if it's a JSON script
              const baseUrl = srcOrHref.split('?')[0];
              if (document.querySelector(`${tagName}[${attributes.key}^="${baseUrl}"]`)) return;
              if (tagName === 'script' && el.getAttribute('type') === 'application/json') return;

              promises.push(new Promise((resolve, reject) => {
                const newEl = document.createElement(tagName);
                Object.entries(attributes).forEach(([key, value]) => newEl[key] = value || srcOrHref);
                newEl.onload = resolve;
                newEl.onerror = reject;
                document.head.appendChild(newEl);
              }));
              el.remove();
            });

            if (promises.length > 0) await Promise.all(promises);
          };

          // Load CSS first, then scripts
          await loadExternalResources('link[rel="stylesheet"]', 'link', { key: 'href', rel: 'stylesheet', href: null });
          await loadExternalResources('script[src]', 'script', { key: 'src', src: null, async: false });

          // NOW inject the HTML and let Alpine initialize
          contentContainer.innerHTML = htmlToCache;
          cachedResults[cacheKey] = htmlToCache;
          
          this.contentLoaded = true;
        }
      } catch (error) {
        console.error('Error loading lens builder content:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    builderPopupFocus() {
      Alpine.store('xFocusElement').trapFocus(this.popupId, 'CloseBuilderPopup');
    },
    
    builderPopupRemoveFocus() {
      const activeElement = document.getElementById('OpenBuilderPopup');
      if (activeElement) {
        Alpine.store('xFocusElement').removeTrapFocus(activeElement);
      }
      this.openBuilderPopup = false;
      Alpine.store('xPopup').close();
      window.dispatchEvent(new CustomEvent('eyeglass-builder:close-popup'));
    },

    open() {
      this.openBuilderPopup = true;
      Alpine.store('xPopup').open = true;
      this.builderPopup = document.getElementById(this.popupId);
      this.loadBuilderContent();
      this.$nextTick(() => {
        this.builderPopupFocus();
      });
    }
  }));

  Alpine.data('yotpoPicturesWidget', (prevArrowHTML, nextArrowHTML) => ({
    init() {
      const container = this.$el;
      let attempts = 0;
      const maxAttempts = 40; // 20 seconds at 500ms

      if (!container._yotpoA11yObserver) {
        container._yotpoA11yObserver = new MutationObserver(() => {
          enhanceYotpoPhotoLabels(container);
        });
        container._yotpoA11yObserver.observe(container, { childList: true, subtree: true });
      }

      const interval = setInterval(() => {
        attempts++;
        this.yotpoWrapper = container.querySelectorAll('.yotpo-display-wrapper');
        
        if (this.yotpoWrapper.length > 0) {
          this.yotpoWrapper.forEach(wrapper => {
            wrapper.classList.add('x-splide');
           if (!wrapper.classList.contains('custom-arrows-added')) {
             if (prevArrowHTML) wrapper.insertAdjacentHTML('beforeend', prevArrowHTML);
             if (nextArrowHTML) wrapper.insertAdjacentHTML('beforeend', nextArrowHTML);
             
             wrapper.classList.add('custom-arrows-added');
           }
           wrapper.addEventListener('click', () => {
             this.updateYotpoLiveRegion();
           });
          });
          enhanceYotpoPhotoLabels(container);
           clearInterval(interval);
        } else if (attempts >= maxAttempts) {
           clearInterval(interval);
        }
      }, 500);
    },
    updateYotpoLiveRegion() {
      setTimeout(() => {
        this.yotpoWrapper.forEach(wrapper => {
          const activeSlide = wrapper.querySelector('.splide__slide.is-active, [class*="active"], .is-active');
          if (activeSlide) {
            const img = activeSlide.querySelector('img');
            const altText = img ? (img.getAttribute('alt') || img.getAttribute('aria-label') || "") : "";
            const slides = Array.from(wrapper.querySelectorAll('.splide__slide:not(.splide__slide--clone)'));
            const activeIndex = slides.indexOf(activeSlide);
            const liveRegion = document.getElementById("global-carousel-live-region") || document.getElementById("yotpo-live-region");
            if (liveRegion && activeIndex >= 0) {
              liveRegion.textContent = "";
              setTimeout(() => {
                liveRegion.textContent = `Slide ${activeIndex + 1} of ${slides.length}` + (altText ? `: ${altText}` : "");
              }, 100);
            }
          }
        });
      }, 500);
    },
    makeSlide(slide){
      this.yotpoWrapper.forEach(wrapper => {
        const nextArrow = wrapper.querySelector('.arrows-wrapper-right .yotpo-icon-right-arrow-light');
        const prevArrow = wrapper.querySelector('.arrows-wrapper-left .yotpo-icon-left-arrow-light');
        if(nextArrow && slide == 1){
          nextArrow.click();
        }
        if(prevArrow && slide == 0){
          prevArrow.click();
        }
      });
      this.updateYotpoLiveRegion();
    }
  }));

  function applyYotpoLabelTemplate(template, vars) {
    if (!template) return '';
    let label = template;
    Object.keys(vars).forEach((key) => {
      label = label.split(`{{ ${key} }}`).join(String(vars[key]));
    });
    return label.trim();
  }

  function isGenericYotpoPhotoAlt(alt) {
    if (!alt) return true;
    const normalized = alt.toLowerCase().trim();
    return (
      normalized.includes('instagram') ||
      normalized.includes('shopper photo') ||
      normalized.includes('image-thumbnail')
    );
  }

  function enhanceYotpoPhotoLabels(container) {
    const sectionLabel = container.dataset.yotpoSectionLabel || '';
    const photoLabelTemplate = container.dataset.yotpoPhotoLabel || '';
    const photoLabelWithAltTemplate = container.dataset.yotpoPhotoLabelWithAlt || '';
    if (!photoLabelTemplate) return;

    const buttons = Array.from(
      container.querySelectorAll('.y-image-wrapper[role="button"], .y-image-wrapper[tabindex="0"]')
    ).filter((button) => !button.closest('.splide__slide--clone'));

    buttons.forEach((button, index) => {
      const photoIndex = index + 1;
      const total = buttons.length;
      const img = button.querySelector('img');
      const alt = img?.getAttribute('alt')?.trim() || '';
      const vars = { index: photoIndex, total, section: sectionLabel };
      let label;

      if (alt && !isGenericYotpoPhotoAlt(alt) && photoLabelWithAltTemplate) {
        label = applyYotpoLabelTemplate(photoLabelWithAltTemplate, { ...vars, description: alt });
      } else {
        label = applyYotpoLabelTemplate(photoLabelTemplate, vars);
      }

      if (label) button.setAttribute('aria-label', label);
    });
  }

  /**
   * xBrowser Alpine Component
   * -------------------------
   * Detects the user's Operating System (OS) and Browser and applies corresponding CSS classes
   * to the element where this component is initialized.
   * 
   * Usage:
   * <div x-data="xBrowser"> ... </div>
   * 
   * Classes added:
   * - OS: 'windows', 'mac', 'android'
   * - Browser: 'isChrome', 'isFirefox', 'isSafari', 'isEdge', 'isIE', 'isOpera', 'isBlink'
   */
  Alpine.data('xBrowser', () => ({
    os: null,
    browser: null,

    init() {
      this.os = this.getOS();
      this.browser = this.getBrowser();
      this.applyClasses();
    },

    getOS() {
      const ua = navigator.userAgent;
      if (ua.includes("Android")) return "android";
      if (ua.includes("Mac")) return "mac";
      return "windows";
    },

    getBrowser() {
      const ua = navigator.userAgent;
      const isOpera = (!!window.opr && !!opr.addons) || !!window.opera || ua.indexOf(' OPR/') >= 0;
      const isFirefox = typeof InstallTrigger !== 'undefined';
      const isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && window['safari'].pushNotification));
      const isIE = /*@cc_on!@*/ false || !!document.documentMode;
      const isEdge = !isIE && !!window.StyleMedia;
      const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
      const isEdgeChromium = isChrome && (ua.indexOf("Edg") !== -1);

      if (isFirefox) return 'isFirefox';
      if (isEdgeChromium) return 'isEdgeChromium';
      if (isChrome) return 'isChrome';
      if (isSafari) return 'isSafari';
      if (isOpera) return 'isOpera';
      if (isIE) return 'isIE';
      if (isEdge) return 'isEdge';
      return 'isBlink';
    },

    applyClasses() {
      if (this.os) this.$el.classList.add(this.os);
      if (this.browser) this.$el.classList.add(this.browser);
    }
  }));

  // Yotpo Lightbox Modal accessibility updates using MutationObserver
  let lastAnnouncedYotpoImg = '';
  let yotpoLiveRegionTimeout = null;

  const yotpoObserver = new MutationObserver(() => {
    const modal = document.querySelector('.yotpo-modal-mask, .yotpo-lightbox-container, .yotpo-modal');
    if (modal && (modal.classList.contains('yotpo-active-display') || modal.classList.contains('yotpo-modal-active') || window.getComputedStyle(modal).display !== 'none')) {
      const img = modal.querySelector('img');
      const imgUrl = img ? img.src : '';
      if (imgUrl && imgUrl !== lastAnnouncedYotpoImg) {
        lastAnnouncedYotpoImg = imgUrl;

        const username = modal.querySelector('[class*="username"], .yotpo-header-title')?.textContent?.trim() || "";
        const caption = modal.querySelector('[class*="caption"], .yotpo-text, .yotpo-caption')?.textContent?.trim() || "";
        const altText = img.getAttribute('alt') || "";

        let statusText = "Image updated";
        if (username || caption) {
          statusText = (username ? `Photo by ${username}` : "Photo") + (caption ? `: ${caption}` : "");
        } else if (altText) {
          statusText = `Image: ${altText}`;
        }

        const liveRegion = document.getElementById("global-carousel-live-region") || document.getElementById("yotpo-live-region");
        if (liveRegion) {
          liveRegion.textContent = '';
          if (yotpoLiveRegionTimeout) {
            clearTimeout(yotpoLiveRegionTimeout);
          }
          setTimeout(() => {
            liveRegion.textContent = statusText;
            yotpoLiveRegionTimeout = setTimeout(() => {
              liveRegion.textContent = "";
            }, 5000);
          }, 100);
        }
      }
    }
  });

  yotpoObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'class']
  });

  // shopify-payment-terms shadow DOM logo accessibility update
  function updateShopPayAriaLabel(element) {
    const shadowRoot = element.shadowRoot;
    if (shadowRoot) {
      const svg = shadowRoot.querySelector('svg[aria-label="Shop Pay"]');
      if (svg) {
        svg.setAttribute('aria-label', 'Shop Pay logo');
        return true;
      }
    }
    return false;
  }

  function handlePaymentTerms(element) {
    if (element.hasAttribute('data-aria-label-updated')) return;

    if (updateShopPayAriaLabel(element)) {
      element.setAttribute('data-aria-label-updated', 'true');
      return;
    }

    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (updateShopPayAriaLabel(element)) {
        element.setAttribute('data-aria-label-updated', 'true');
        clearInterval(interval);
      } else if (attempts >= 20) {
        clearInterval(interval);
      }
    }, 100);
  }

  const paymentTermsObserver = new MutationObserver(() => {
    document.querySelectorAll('shopify-payment-terms').forEach(handlePaymentTerms);
  });

  paymentTermsObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Initial run
  document.querySelectorAll('shopify-payment-terms').forEach(handlePaymentTerms);
});
